<script setup>
import { ref } from "vue";
import { Head, usePage } from '@inertiajs/vue3';
import { trans } from 'laravel-vue-i18n';
import UnauthLayout from "@/Layouts/UnauthLayout.vue";
import Footer from '@/Components/Footer.vue';

const page = usePage();
const localizedPath = (path) => {
    const locale = page.props.locale;
    return locale === 'ro' ? path : `/${locale}${path}`;
};

const faqs = ref([
    {
        question: trans('public.csrd.faq.q1'),
        answer: trans('public.csrd.faq.a1')
    },
    {
        question: trans('public.csrd.faq.q2'),
        answer: trans('public.csrd.faq.a2')
    },
    {
        question: trans('public.csrd.faq.q3'),
        answer: trans('public.csrd.faq.a3')
    }
]);

const openFaq = ref(null);

const toggleFaq = (index) => {
    openFaq.value = openFaq.value === index ? null : index;
};
</script>

<template>
    <UnauthLayout>
        <Head>
            <title>{{ trans('public.csrd.meta.title') }}</title>
            <meta
                name="description"
                :content="trans('public.csrd.meta.description')"
            />
            <link rel="icon" type="image/svg+xml" href="/images/logo-seeo2.svg" />
            <meta property="og:title" :content="trans('public.csrd.meta.title')" />
            <meta property="og:description" :content="trans('public.csrd.meta.description')" />
            <meta property="og:image" content="/images/og-image.jpg" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="SeeO2" />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:title" :content="trans('public.csrd.meta.title')" />
            <meta property="twitter:description" :content="trans('public.csrd.meta.description')" />
            <meta property="twitter:image" content="/images/og-image.jpg" />
        </Head>
        <div class="h-full csrd">
            <!-- Hero Section -->
            <section class="bg-white py-32" id="csrd-intro">
                <div class="container mx-auto px-4 max-w-7xl">
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        <div class="relative">
                            <h1 class="block text-4xl lg:text-6xl font-bold text-teal-700 mb-6 circle-bg relative z-10">
                                {{ trans('public.csrd.hero.title') }}
                            </h1>
                        </div>
                        <div class="flex flex-col justify-center">
                            <p class="text-xl text-teal-700 mb-12 leading-relaxed">
                                {{ trans('public.csrd.hero.description') }}
                            </p>
                            <div>
                                <a
                                    :href="localizedPath('/book-meeting')"
                                    class="px-6 py-3 bg-teal-500 text-white rounded-full text-lg font-medium hover:bg-teal-600 transition"
                                >
                                    {{ trans('public.csrd.hero.cta') }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="banner" class="relative">
                <div class="grid grid-cols-1 lg:grid-cols-2">
                    <div class="h-[300px] lg:h-[500px]">
                        <img src="/images/csrd-coperta.jpg" alt="CSRD Cover" class="w-full h-full object-cover">
                    </div>
                    <div class="bg-[#99F6E4] h-[300px] lg:h-[500px] flex items-center p-6 lg:px-12">
                        <div>
                            <div class="inline-block bg-white rounded-full px-4 py-1 text-sm font-medium text-teal-700 mb-4 lg:mb-6">
                                {{ trans('public.csrd.banner.label') }}
                            </div>
                            <h2 class="text-2xl lg:text-4xl xl:text-5xl font-bold text-teal-700 mb-4 lg:mb-6">
                                {{ trans('public.csrd.banner.title') }}
                            </h2>
                            <p class="text-lg lg:text-xl text-teal-700">
                                {{ trans('public.csrd.banner.description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- What is CSRD Section -->
            <section id="what-is-csrd" class="relative py-8 mt-32">
                <div class="absolute right-0 top-0 w-full lg:w-3/4 h-full bg-[#115E59]"></div>
                <div class="container mx-auto px-4 max-w-7xl relative">
                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16">
                        <div class="relative hidden lg:block">
                        </div>
                        <div class="col-span-1 lg:col-span-3 py-12 text-white">
                            <h2 class="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-white">
                                {{ trans('public.csrd.what.title') }}
                            </h2>
                            <p class="text-lg leading-relaxed mb-8">
                                {{ trans('public.csrd.what.description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            

            <section id="more-about-csrd">
                <div class="container mx-auto px-4 max-w-3xl py-16">
                    <div class="flex flex-col items-start border-l-4 border-[#115E59] pl-8">
                        <span class="text-[#115E59] text-6xl font-serif mb-8">"</span>
                        <h3 class="text-[#115E59] text-2xl font-bold mb-6">
                            {{ trans('public.csrd.quote.title') }}
                        </h3>
                        <p class="text-gray-600 text-lg mb-6">
                            {{ trans('public.csrd.quote.content1') }}
                        </p>
                        <p class="text-gray-600 text-lg mb-6">
                            {{ trans('public.csrd.quote.content2') }}
                        </p>
                        <p class="text-gray-600 text-lg mb-6">
                            {{ trans('public.csrd.quote.content3') }}
                        </p>
                        <p class="text-gray-600 text-lg mb-6">
                            {{ trans('public.csrd.quote.content4') }}
                        </p>
                        <p class="text-gray-600 text-lg mb-6">
                            {{ trans('public.csrd.quote.content5') }}
                        </p>
                        <p class="text-gray-600 text-lg mb-8">
                            {{ trans('public.csrd.quote.content6') }}
                        </p>
                    </div>
                </div>
            </section>

            <section class="relative py-8 mt-16" id="csrd-details">
                <div class="absolute left-0 top-0 w-full lg:w-3/4 h-full bg-[#115E59]"></div>
                <div class="container mx-auto px-4 max-w-7xl relative">
                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16">
                        <div class="col-span-1 lg:col-span-3 py-12 text-white">
                            <h2 class="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-white">
                                {{ trans('public.csrd.details.title') }}
                            </h2>
                            <p class="text-lg mb-12">
                                {{ trans('public.csrd.details.description') }}
                            </p>
                            <div class="mt-8">
                                <a :href="localizedPath('/book-meeting')" class="inline-flex items-center px-6 py-3 border-2 border-white rounded-full text-lg font-medium hover:bg-white hover:text-[#115E59] transition">
                                    {{ trans('public.csrd.details.cta') }}
                                </a>
                            </div>
                        </div>
                        <div class="relative hidden lg:block">
                        </div>
                    </div>
                </div>
            </section>
            <!-- CSRD Benefits Section -->
            <section class="py-20" id="benefits">
                <div class="container mx-auto px-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div class="relative group overflow-hidden rounded-lg">
                            <div class="aspect-w-16 aspect-h-12 h-full">
                                <img src="/images/card1.jpg" alt="CSRD Card 1" 
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110">
                            </div>
                            <div class="absolute inset-0 bg-gradient-to-t from-[#004432]/90 to-transparent p-8">
                                <div class="absolute bottom-8 left-8 right-8">
                                    <h3 class="text-2xl font-bold text-white h-16">
                                        {{ trans('public.csrd.benefits.1.title') }}
                                    </h3>
                                    <p class="text-white/90 text-sm h-20">
                                        {{ trans('public.csrd.benefits.1.description') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="relative group overflow-hidden rounded-lg">
                            <div class="aspect-w-16 aspect-h-12 h-full">
                                <img src="/images/card2.jpg" alt="CSRD Card 2" 
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110">
                            </div>
                            <div class="absolute inset-0 bg-gradient-to-t from-[#004432]/90 to-transparent p-8">
                                <div class="absolute bottom-8 left-8 right-8">
                                    <h3 class="text-2xl font-bold text-white h-16">
                                        {{ trans('public.csrd.benefits.2.title') }}
                                    </h3>
                                    <p class="text-white/90 text-sm h-20">
                                        {{ trans('public.csrd.benefits.2.description') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="relative group overflow-hidden rounded-lg">
                            <div class="aspect-w-16 aspect-h-12 h-full">
                                <img src="/images/card3.jpg" alt="CSRD Card 3" 
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110">
                            </div>
                            <div class="absolute inset-0 bg-gradient-to-t from-[#004432]/90 to-transparent p-8">
                                <div class="absolute bottom-8 left-8 right-8">
                                    <h3 class="text-2xl font-bold text-white h-16">
                                        {{ trans('public.csrd.benefits.3.title') }}
                                    </h3>
                                    <p class="text-white/90 text-sm h-20">
                                        {{ trans('public.csrd.benefits.3.description') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <!-- FAQ Section -->
            <section class="relative py-24" id="faq">
                <div class="container mx-auto px-4 max-w-5xl">
                    <div class="space-y-8">
                        <div v-for="(faq, index) in faqs" :key="index" 
                            class="border-b border-[#56BF98] pb-8">
                            <button 
                                @click="toggleFaq(index)"
                                class="w-full text-left"
                            >
                                <div class="flex justify-between items-center">
                                    <h3 class="text-xl font-semibold text-teal-700">{{ faq.question }}</h3>
                                    <span class="text-2xl text-teal-700">{{ openFaq === index ? '−' : '+' }}</span>
                                </div>
                            </button>
                            <div v-show="openFaq === index" class="mt-4 text-gray-600">
                                {{ faq.answer }}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <Footer />
    </UnauthLayout>
</template>

<style scoped>
.circle-bg::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background-color: #99F6E4;
    border-radius: 9999px;
    left: -20px;
    top: -20px;
    z-index: -10;
    opacity: 0.5;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}
</style>
